import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, ChevronRight, Trophy } from "lucide-react";
import { toast } from "sonner";
import { MODULES } from "../data/modules";
import { useProgress } from "../context/ProgressContext";
import { PromptPlayground } from "../components/PromptPlayground";
import { Quiz } from "../components/Quiz";
import { PassportBadge } from "../components/PassportBadge";

export default function Module() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const module_ = useMemo(() => MODULES.find((m) => m.id === moduleId), [moduleId]);
  const { progress, markLessonComplete, awardBadge, setModuleStep } = useProgress();

  const [step, setStep] = useState(0);
  const [stepDone, setStepDone] = useState(false);

  useEffect(() => {
    if (!module_) return;
    const savedStep = progress.module_progress?.[moduleId] || 0;
    // Skip past completed lessons to next unfinished (bounded to length-1)
    const firstIncomplete = module_.lessons.findIndex(
      (l) => !progress.completed_lessons?.includes(l.id)
    );
    const target =
      firstIncomplete === -1 ? module_.lessons.length - 1 : Math.max(firstIncomplete, savedStep);
    setStep(target);
    // eslint-disable-next-line
  }, [moduleId]);

  if (!module_) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center">
        <h1 className="font-serif-display text-4xl">Module not found</h1>
        <Link to="/" className="text-primary underline mt-4 inline-block">Back home</Link>
      </div>
    );
  }

  const lesson = module_.lessons[step];
  const totalSteps = module_.lessons.length;
  const isLast = step === totalSteps - 1;
  const alreadyDone = progress.completed_lessons?.includes(lesson.id);

  const completeStep = (xpGain = 20) => {
    if (stepDone || alreadyDone) return;
    markLessonComplete(lesson.id, xpGain);
    setStepDone(true);
    toast.success(`+${xpGain} XP`, { description: lesson.title });
    if (lesson.type === "recap") {
      if (module_.badge && !progress.badges?.includes(module_.badge)) {
        awardBadge(module_.badge);
        setTimeout(() => {
          toast.success("New passport stamp", { description: module_.badge });
        }, 400);
      }
    }
  };

  const next = () => {
    if (isLast) {
      navigate("/");
      return;
    }
    const nx = Math.min(step + 1, totalSteps - 1);
    setStep(nx);
    setStepDone(false);
    setModuleStep(moduleId, nx);
  };
  const prev = () => {
    const px = Math.max(step - 1, 0);
    setStep(px);
    setStepDone(false);
  };

  const canProceed = stepDone || alreadyDone || lesson.type === "concept" || lesson.type === "recap";

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
      {/* header */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/" data-testid="module-back-btn" className="inline-flex items-center gap-2 font-mono-code text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">
          <ArrowLeft className="w-4 h-4" /> back to modules
        </Link>
        <div className="font-mono-code text-xs text-muted-foreground">
          {step + 1} / {totalSteps}
        </div>
      </div>

      {/* progress bar */}
      <div className="h-1 bg-border relative overflow-hidden mb-8" data-testid="module-progress-bar">
        <motion.div
          className="absolute inset-y-0 left-0 bg-primary"
          initial={false}
          animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* header block */}
      <div className="mb-10">
        <div className="font-mono-code text-[10px] uppercase tracking-widest text-primary mb-2">
          module {module_.number} · {lesson.type}
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight" data-testid="module-title">
          {module_.title}
        </h1>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={lesson.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35 }}
        >
          {lesson.type === "concept" && (
            <div className="grid md:grid-cols-12 gap-10">
              <div className="md:col-span-7 border-l-2 border-primary pl-6 md:pl-10">
                <h2 className="font-serif-display text-3xl md:text-4xl mb-6" data-testid="concept-title">
                  {lesson.title}
                </h2>
                <p className="text-base md:text-lg leading-relaxed text-foreground/90">
                  {lesson.body}
                </p>
                <div className="mt-8 bg-surface border border-border p-6">
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-primary mb-2">
                    takeaway
                  </div>
                  <div className="font-medium">{lesson.takeaway}</div>
                </div>
              </div>
              <aside className="md:col-span-5">
                <div className="border border-border p-6 md:p-8 bg-background">
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                    module overview
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">{module_.intro}</p>
                  <ul className="space-y-2 text-sm">
                    {module_.lessons.map((l, i) => (
                      <li
                        key={l.id}
                        className={
                          "flex items-center gap-2 " +
                          (i === step ? "text-primary font-medium" : "text-muted-foreground")
                        }
                      >
                        {progress.completed_lessons?.includes(l.id) ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                        <span className="truncate">{l.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          )}

          {lesson.type === "quiz" && (
            <div className="grid md:grid-cols-12 gap-10">
              <div className="md:col-span-7">
                <Quiz lesson={lesson} onCorrect={() => completeStep(15)} />
              </div>
              <aside className="md:col-span-5">
                <div className="border border-border p-6 bg-surface">
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-primary mb-2">
                    knowledge check
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Pick the best answer. Read the explanation carefully — quizzes are where the concepts actually stick.
                  </p>
                </div>
              </aside>
            </div>
          )}

          {lesson.type === "scenario" && (
            <div className="grid md:grid-cols-12 gap-8 md:gap-10">
              <div className="md:col-span-5">
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-primary mb-2">
                  scenario
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl mb-6" data-testid="scenario-title">
                  {lesson.title}
                </h2>
                <p className="text-base leading-relaxed mb-6">{lesson.scenario}</p>
                <div className="border border-border p-5 bg-surface">
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                    rubric · what we score
                  </div>
                  <ul className="space-y-2 text-sm">
                    {lesson.rubric.map((r, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-primary font-mono-code text-xs mt-0.5">{i + 1}</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="md:col-span-7">
                <PromptPlayground
                  lesson={lesson}
                  moduleId={moduleId}
                  onScoreEarned={(res) => {
                    completeStep(res.xp_awarded);
                    if (res.score >= 85 && !progress.badges?.includes("sharp-shooter")) {
                      awardBadge("sharp-shooter");
                      setTimeout(() => toast.success("Passport stamp: Sharp Shooter"), 500);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {lesson.type === "recap" && (
            <div className="grid md:grid-cols-12 gap-10 items-start">
              <div className="md:col-span-7">
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-primary mb-2">
                  module recap
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl mb-8">{lesson.title}</h2>
                <ul className="space-y-4">
                  {lesson.points.map((p, i) => (
                    <li key={i} className="flex gap-4 items-start border-l-2 border-primary pl-4 py-1">
                      <span className="font-serif-display text-3xl text-primary leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-base leading-relaxed pt-1.5">{p}</span>
                    </li>
                  ))}
                </ul>
                {!alreadyDone && !stepDone && (
                  <button
                    data-testid="recap-claim-btn"
                    onClick={() => completeStep(30)}
                    className="mt-8 bg-primary text-primary-foreground px-5 py-2.5 font-medium border-2 border-primary hard-shadow-ink inline-flex items-center gap-2"
                  >
                    <Trophy className="w-4 h-4" /> Claim +30 XP & Stamp
                  </button>
                )}
              </div>
              <div className="md:col-span-5 flex flex-col items-center justify-center border border-border p-10 bg-surface">
                <PassportBadge
                  label={module_.badge}
                  sub="PROMPT CRAFT"
                  earned={progress.badges?.includes(module_.badge) || stepDone}
                  size={140}
                  testId="recap-badge"
                />
                <div className="mt-6 font-serif-display text-2xl text-center">{module_.badge}</div>
                <div className="text-sm text-muted-foreground text-center mt-1">
                  Awarded for completing {module_.title}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="mt-14 flex items-center justify-between border-t border-border pt-6">
        <button
          data-testid="lesson-prev-btn"
          onClick={prev}
          disabled={step === 0}
          className="inline-flex items-center gap-2 px-4 py-2 font-mono-code text-xs uppercase tracking-widest text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" /> previous
        </button>
        <button
          data-testid="lesson-next-btn"
          onClick={next}
          disabled={!canProceed}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 font-medium border-2 border-primary hard-shadow-ink disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:transform-none"
        >
          {isLast ? "Finish Module" : "Next"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
